import React, { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Calendar,
  Clock,
} from "lucide-react";
import { BLOG_POSTS } from "../data/mock";
import { useRevealGroup } from "../hooks/useReveal";
import {
  PillBadge,
  CTASection,
  ScheduleCallButton,
} from "../components/Shared";
import { useQuote } from "../components/QuoteModal";

const ARTICLE_DISCLAIMER =
  "This article is for general information only and is not financial, legal or insurance advice. Program rules, coverage requirements, underwriting standards and rates change over time and vary by insurer and by individual circumstances. Figures and examples are illustrative. Verify current requirements with official Government of Canada resources where immigration programs are involved, and speak with a licensed insurance advisor before making any decision.";

function useArticleMetadata(post) {
  useEffect(() => {
    const previousTitle = document.title;
    const existing = document.head.querySelector(
      'meta[name="description"]'
    );
    const previousDescription =
      existing?.getAttribute("content") ?? null;

    const meta = existing || document.createElement("meta");

    if (!existing) {
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }

    document.title = post.metaTitle;
    meta.setAttribute("content", post.metaDescription);

    return () => {
      document.title = previousTitle;

      if (!existing) {
        meta.remove();
      } else if (previousDescription === null) {
        meta.removeAttribute("content");
      } else {
        meta.setAttribute("content", previousDescription);
      }
    };
  }, [post.metaTitle, post.metaDescription]);
}

// Render contact links without raw HTML or a self-referencing component.
function renderArticleLinks(text, prefix) {
  const parts = text.split(
    /(\[[^\]]+\]\(mailto[^)]*\)|\(604\) 996 6862)/g
  );

  return parts.map((part, index) => {
    const key = `${prefix}-${index}`;
    const emailLink = part.match(
      /^\[([^\]]+)\]\(mailto[^)]*\)$/
    );

    if (emailLink) {
      return (
        <a
          key={key}
          href="mailto:info@abhifinancial.com"
          className="break-words text-[#006FB9] hover:underline"
        >
          {emailLink[1]}
        </a>
      );
    }

    if (part === "(604) 996 6862") {
      return (
        <a
          key={key}
          href="tel:+16049966862"
          className="text-[#006FB9] hover:underline"
        >
          {part}
        </a>
      );
    }

    return (
      <React.Fragment key={key}>
        {part}
      </React.Fragment>
    );
  });
}

// Preserve supplied bold text, including contact links inside bold text.
function ArticleText({ text }) {
  const parts = text.split(/(\*\*.*?\*\*)/g);

  return parts.map((part, index) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return (
        <strong key={index} className="font-semibold">
          {renderArticleLinks(
            part.slice(2, -2),
            `bold-${index}`
          )}
        </strong>
      );
    }

    return (
      <React.Fragment key={index}>
        {renderArticleLinks(part, `plain-${index}`)}
      </React.Fragment>
    );
  });
}

function BlogPostContent({ post }) {
  const ref = useRevealGroup();
  const { openQuote } = useQuote();

  useArticleMetadata(post);

  const related = post.related
    .map((entry) => {
      const article = BLOG_POSTS.find(
        (item) => item.slug === entry.slug
      );

      return article
        ? { ...article, label: entry.label }
        : null;
    })
    .filter(Boolean);

  return (
    <div ref={ref}>
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#073B67]">
        <img
          src={post.image}
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-25"
        />

        <div className="absolute inset-0 bg-gradient-to-b from-[#073B67]/70 to-[#073B67]" />

        {/* Same max width and horizontal spacing as the other pages */}
        <div className="relative mx-auto max-w-7xl px-6 py-20 lg:py-24">
          {/* Keeps long blog headings readable while remaining left aligned */}
          <div className="hero-stagger max-w-4xl">
            <Link
              to="/blog"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#B9DFF3] transition-colors hover:text-white"
              data-testid="back-to-blog"
            >
              <ArrowLeft className="h-4 w-4" />
              All Insights
            </Link>

            <div className="mt-5">
              <PillBadge light>{post.category}</PillBadge>
            </div>

            <h1 className="mt-4 font-heading text-3xl font-extrabold leading-[1.15] tracking-tight text-white sm:text-4xl lg:text-[44px]">
              {post.title}
            </h1>

            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-3 text-sm text-[#C5DEEC]">
              <span className="flex items-center gap-1.5">
                <Calendar className="h-4 w-4 shrink-0" />
                {post.date}
              </span>

              <span className="flex items-center gap-1.5">
                <Clock className="h-4 w-4 shrink-0" />
                {post.readTime}
              </span>
            </div>

            <div className="mt-6 flex items-center gap-3">
              <img
                src={post.authorImage}
                alt={post.author}
                className="h-12 w-12 shrink-0 rounded-full object-cover object-top ring-2 ring-[#B9DFF3]/40"
              />

              <div className="min-w-0 text-sm leading-relaxed">
                <p className="font-semibold text-white">
                  {post.author}
                </p>

                <p className="text-[#C5DEEC]">
                  {post.authorRole}, {post.authorOrganization}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BODY */}
      <section className="bg-white py-16 lg:py-20">
        {/* Same outer width and left-right spacing as Home page */}
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-6 lg:grid-cols-[minmax(0,1fr)_320px]">
          <article className="min-w-0 max-w-none" data-reveal>
            <div className="overflow-hidden rounded-3xl">
              <img
                src={post.image}
                alt={post.title}
                className="h-80 w-full object-cover"
              />
            </div>

            <div className="mt-10 space-y-6">
              {post.content.map((block, index) => {
                if (block.type === "h2") {
                  return (
                    <h2
                      key={index}
                      className="pt-2 font-heading text-xl font-extrabold tracking-tight text-[#0B2748] sm:text-2xl"
                    >
                      {block.text}
                    </h2>
                  );
                }

                return (
                  <p
                    key={index}
                    className="text-[15.5px] leading-[1.85] text-[#294A65]"
                  >
                    <ArticleText text={block.text} />
                  </p>
                );
              })}
            </div>

            <div className="mt-8 text-[15.5px] leading-relaxed text-[#294A65]">
              <strong className="font-semibold">
                Related reading:
              </strong>{" "}

              {related.map((item, index) => (
                <React.Fragment key={item.slug}>
                  {index > 0 && " · "}

                  <Link
                    to={`/blog/${item.slug}`}
                    className="text-[#006FB9] hover:underline"
                  >
                    {item.label}
                  </Link>
                </React.Fragment>
              ))}
            </div>

            <div className="mt-10 rounded-2xl bg-[#F4F7FB] px-6 py-5">
              <p className="text-xs leading-relaxed text-[#70879A]">
                <strong>Disclaimer:</strong>{" "}
                {ARTICLE_DISCLAIMER}
              </p>
            </div>
          </article>

          {/* SIDEBAR */}
          <aside className="space-y-6 lg:sticky lg:top-28 lg:self-start">
            <div
              data-reveal
              className="rounded-2xl bg-[#006FB9] p-7 text-white"
            >
              <h4 className="font-heading text-lg font-bold">
                Have questions about this topic?
              </h4>

              <p className="mt-2 text-sm leading-relaxed text-[#C5DEEC]">
                Get a free consultation with a licensed advisor — 7 days
                a week.
              </p>

              <button
                type="button"
                onClick={() => openQuote()}
                className="btn-light group mt-5 w-full justify-center"
                data-testid="post-sidebar-quote"
              >
                Get a Free Quote
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>

              <ScheduleCallButton
                className="btn-ghost-light mt-4 w-full justify-center"
                testId="blog-schedule-call"
              />
            </div>

            <div
              data-reveal
              className="rounded-2xl border border-[#E1ECF3] p-7"
            >
              <h4 className="font-heading text-base font-bold text-[#0B2748]">
                Related Reading
              </h4>

              <div className="mt-4 space-y-4">
                {related.map((item) => (
                  <Link
                    key={item.slug}
                    to={`/blog/${item.slug}`}
                    className="group flex gap-3.5"
                  >
                    <img
                      src={item.image}
                      alt={item.title}
                      className="h-16 w-20 shrink-0 rounded-xl object-cover"
                    />

                    <div className="min-w-0">
                      <p className="text-sm font-bold leading-snug text-[#193B58] transition-colors duration-300 group-hover:text-[#0785C7]">
                        {item.title}
                      </p>

                      <p className="mt-1 text-xs text-[#70879A]">
                        {item.readTime}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </section>

      <CTASection title="Protect What Matters Most" />
    </div>
  );
}

export default function BlogPost() {
  const { slug } = useParams();

  const post = BLOG_POSTS.find(
    (item) => item.slug === slug
  );

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  // Reinitialize metadata and reveal observers when article routes change.
  return (
    <BlogPostContent
      key={post.slug}
      post={post}
    />
  );
}